
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import { getGroups } from "@/lib/storage";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import ProductFormBasicInfo from "@/components/product/ProductFormBasicInfo";
import ProductFormInventory from "@/components/product/ProductFormInventory";
import ProductFormGroups from "@/components/product/ProductFormGroups";
import ProductFormActions from "@/components/product/ProductFormActions";
import ProductFormSupplier from "@/components/product/ProductFormSupplier";
import { useProductForm } from "@/hooks/use-product-form";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import { useEffect, useState } from "react";

const AddProductPage = () => {
  const navigate = useNavigate();
  const groups = getGroups();
  const { toast } = useToast();
  const { user, isLoading } = useAuth();
  const [redirecting, setRedirecting] = useState(false);
  
  // Check authentication
  useEffect(() => {
    if (!isLoading && !user) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to add products",
        variant: "destructive",
      });
      navigate("/auth");
    }
  }, [user, isLoading, navigate, toast]);
  
  const {
    formState: {
      name,
      description,
      category,
      currentStock,
      usageRate,
      minStockAlert,
      selectedGroups,
      packageSize,
      supplierName,
      supplierWebsite,
      supplierContact,
      productCode,
      isSubmitting,
    },
    setters: {
      setName,
      setDescription,
      setCategory,
      setCurrentStock,
      setUsageRate,
      setMinStockAlert,
      setPackageSize,
      setSupplierName,
      setSupplierWebsite,
      setSupplierContact,
      setProductCode,
    },
    handleGroupToggle,
    handleSubmit,
    isSubmitDisabled,
  } = useProductForm();

  // Custom form submit handler
  const onSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to add products",
        variant: "destructive",
      });
      navigate("/auth");
      return;
    }

    if (isSubmitDisabled || isSubmitting || redirecting) {
      return;
    }

    setRedirecting(true);
    
    try {
      console.log("Form submitted, calling handleSubmit...");
      await handleSubmit();
      console.log("Product saved successfully");
      // The form's handleSubmit already shows success toast
    } catch (error) {
      console.error("Error submitting product:", error);
      toast({
        title: "Error",
        description: "Failed to save product. Please try again.",
        variant: "destructive",
      });
      setRedirecting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <main className="page-container max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/products")}
            className="mr-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">Add New Product</h1>
        </div>

        <form onSubmit={onSubmitForm}>
          <Card>
            <CardHeader>
              <CardTitle>Product Details</CardTitle>
              <CardDescription>
                Enter information about the product you want to track
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ProductFormBasicInfo
                name={name}
                setName={setName}
                description={description}
                setDescription={setDescription}
                category={category}
                setCategory={setCategory}
              />
              
              <ProductFormInventory
                currentStock={currentStock}
                setCurrentStock={setCurrentStock}
                usageRate={usageRate}
                setUsageRate={setUsageRate}
                minStockAlert={minStockAlert}
                setMinStockAlert={setMinStockAlert}
                packageSize={packageSize}
                setPackageSize={setPackageSize}
              />
              
              <ProductFormSupplier
                supplierName={supplierName}
                setSupplierName={setSupplierName}
                supplierWebsite={supplierWebsite}
                setSupplierWebsite={setSupplierWebsite}
                supplierContact={supplierContact}
                setSupplierContact={setSupplierContact}
                productCode={productCode}
                setProductCode={setProductCode}
              />
              
              <ProductFormGroups
                groups={groups}
                selectedGroups={selectedGroups}
                handleGroupToggle={handleGroupToggle}
              />
            </CardContent>
            <CardFooter>
              <ProductFormActions
                onCancel={() => navigate("/products")}
                isSubmitting={isSubmitting}
                isSubmitDisabled={isSubmitDisabled || isSubmitting || redirecting}
              />
            </CardFooter>
          </Card>
        </form>
      </main>
    </div>
  );
};

export default AddProductPage;
